/* =====================================================================================
   Circus -- competitive intelligence.

   Named from "not my circus, not my monkeys, but we know the clowns."

   THE ORGANISING DISTINCTION IS WHAT A COMPANY SELLS, NOT WHAT IT MAKES. Kits are bought by a
   laboratory that runs them itself; a service is a laboratory sending samples away. They are
   different buyers, different budgets and different sales motions, and a company can move
   between them: CareDx sold its Lab Products division to Eurobio Scientific in June 2026 and
   went from both to service. A competitive file that cannot express that change cannot
   describe this market.

   FOOTPRINT IS DERIVED, NOT MAINTAINED. Where a competitor actually is comes from what the
   field already records against centres -- incumbent vendors and per-method vendor records --
   so the picture updates as a side effect of ordinary account work. A separately maintained
   copy would disagree with the field within a month.

   PUBLIC SOURCES ONLY, and the database requires one on every record. Confidentially obtained
   competitor information is a legal problem for whoever holds it, not an advantage.

   THE PAGE IS A GRID OF SMALL SUMMARY CARDS, one per competitor: name, threat, what they
   sell, and how much is on file. Opening a card shows the full intelligence record in an
   overlay. The full record used to BE the grid, which meant thirty competitors was a wall
   of prose nobody scanned; the summary is for scanning and the record is for reading.

   Each card also carries "Add document" directly, so filing a deck picked up at a congress
   does not require opening and reading the whole record first. That screen lists what is
   already filed, name and description, BEFORE accepting anything, and warns when the same
   screen is open in another tab, because two tabs that cannot see each other's uploads are
   how the same deck gets filed twice.
   ===================================================================================== */

import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import {
  fetchCompetitors, fetchOfferings, fetchCompetitorEvents, fetchPresence, saveCompetitor,
  linkVendorAlias,
  CATEGORIES, MODELS,
  type Competitor, type Offering, type CompetitorEvent, type Presence,
} from '../lib/arena';
import { onFail } from '../lib/errors';
import { useDialog } from '../lib/ui';
import { RecordEvidence } from '../components/RecordEvidence';
import { MarketBar, useMarket } from '../components/MarketBar';
import { Boundary } from '../components/Boundary';
import { SearchBuilder } from '../components/SearchBuilder';
import { competitorFields, COMPETITOR_PRESETS,
         type SearchableCompetitor } from '../lib/searchFields';
import { Attachments } from '../components/Attachments';
import {
  fetchAttachments, fetchAttachmentCounts, uploadFile, signedUrl, removeAttachment,
  humanSize, type Attachment, type AttachmentRole,
} from '../lib/uploads';
import { useTabPresence } from '../lib/tabPresence';
import { BACKEND } from '../lib/db';
import './circus.css';

const THREAT_TONE: Record<string, string> = {
  primary: 'risk', significant: 'warn', watch: 'info', peripheral: 'mute', unknown: 'mute',
};

export function CircusView() {
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [events, setEvents] = useState<CompetitorEvent[]>([]);
  const [presence, setPresence] = useState<Presence[]>([]);
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Competitor> | null>(null);
  /* Ids rather than rows, so a save inside the overlay re-renders it from the reloaded
     list instead of showing the stale copy it was opened with. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [addingDocsId, setAddingDocsId] = useState<string | null>(null);
  const [modelFilter, setModelFilter] = useState<string>('all');
  const { marketId } = useMarket();

  const refreshDocCounts = useCallback((rows: Competitor[]) => {
    fetchAttachmentCounts('competitor', rows.map((c) => c.id))
      .then(setDocCounts)
      .catch(() => setDocCounts({}));
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchCompetitors(marketId).catch(onFail('Competitors', () => setCompetitors([]))),
      fetchOfferings().catch(onFail('Competitor products', () => setOfferings([]))),
      fetchCompetitorEvents().catch(onFail('Market events', () => setEvents([]))),
      fetchPresence().catch(onFail('Competitor footprint', () => setPresence([]))),
    ]).then(([c, o, e, p]) => {
      if (Array.isArray(c)) { setCompetitors(c); refreshDocCounts(c); }
      if (Array.isArray(o)) setOfferings(o);
      if (Array.isArray(e)) setEvents(e);
      if (Array.isArray(p)) setPresence(p);
    }).finally(() => setLoading(false));
    // marketId is a real dependency: without it the list loads once and the selector appears
    // to do nothing, which teaches the user the control is broken.
  }, [marketId, refreshDocCounts]);
  useEffect(reload, [reload]);

  const shown = useMemo(
    () => competitors.filter((c) => modelFilter === 'all' || c.model === modelFilter),
    [competitors, modelFilter],
  );

  const open = competitors.find((c) => c.id === openId) ?? null;
  const addingDocs = competitors.find((c) => c.id === addingDocsId) ?? null;

  /* Vendors the field has recorded against a centre that Circus does not yet know about. This
     is the most useful thing on the page: it is a competitor somebody met and nobody filed. */
  const unknownVendors = presence.filter((p) => !p.competitor_id && p.recorded_as);
  const [linkMsg, setLinkMsg] = useState<string | null>(null);
  const [finding, setFinding] = useState(false);

  /* Attaching the name to the company that already exists, rather than creating a duplicate.
     Reloads afterwards because the vendor should then disappear from this panel and reappear
     inside the footprint of the competitor it belongs to. */
  async function link(recordedAs: string, competitorId: string) {
    setLinkMsg(null);
    try {
      setLinkMsg(await linkVendorAlias(competitorId, recordedAs));
      await reload();
    } catch (e) {
      setLinkMsg(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="page circus">
      <div className="page-head">
        <h1 className="page-title">Circus</h1>
        <p className="page-sub">
          Who else is in this market, what they actually sell, and where the field has met them.
          Every record carries a public source.
        </p>
      </div>

      {/* The same control as Atlas, and for the same reason: the competitive set is a per-
          market question. Renders nothing for a tenant with one market. */}
      <MarketBar />

      <div className="toolbar">
        <div className="seg">
          <button className={modelFilter === 'all' ? 'on' : ''} onClick={() => setModelFilter('all')}>
            All
          </button>
          {/* Only the models this tenant's competitors actually have. The chips used to list
              the diagnostics set for everyone, so a pharmaceutical tenant was offered "Kits". */}
          {MODELS.filter((m) => m.key !== 'unknown'
                               && competitors.some((c) => c.model === m.key)).map((m) => (
            <button key={m.key} className={modelFilter === m.key ? 'on' : ''}
                    title={m.note} onClick={() => setModelFilter(m.key)}>
              {m.label}
            </button>
          ))}
        </div>
        <button className="btn primary" style={{ marginLeft: 'auto' }}
                onClick={() => setEditing({ model: 'unknown', threat: 'unknown' })}>
          Add a competitor
        </button>
      </div>

      {loading && <p className="quiet-loading">Loading</p>}

      {!loading && !competitors.length && (
        <p className="module-empty">
          No competitors recorded. Circus is where the market’s other players live, with what
          they sell and where the field has met them. Every record needs a public source.
        </p>
      )}

      {/* The most useful thing on this page: somebody met a competitor and nobody filed it. */}
      {unknownVendors.length > 0 && (
        <section className="cx-unknown card">
          <h2>Recorded in the field, not yet in Circus</h2>
          <p>
            The team has recorded these vendors against centres and Circus cannot match them to
            a competitor. Before adding one, check whether it is a company already here under
            another name: GenDx trades under Eurobio Scientific, IMDx under Insight Molecular
            Diagnostics. Creating a second record splits one company's footprint in two and
            understates the incumbent everywhere it appears.
          </p>
          <ul>
            {unknownVendors.map((v) => (
              <li key={v.recorded_as}>
                <strong>{v.recorded_as}</strong>
                <span>{v.sites} centre{v.sites === 1 ? '' : 's'} across {v.countries} countr{v.countries === 1 ? 'y' : 'ies'}</span>
                {/* Two answers, and the safer one is offered first. Most unmatched vendors are
                    a company Circus already holds, written the way the laboratory says it. */}
                <select className="input sel cx-link"
                        value=""
                        aria-label={`Link ${v.recorded_as} to an existing competitor`}
                        onChange={(e) => e.target.value && link(v.recorded_as, e.target.value)}>
                  <option value="">This is already here, as...</option>
                  {[...competitors].sort((a, b) => a.name.localeCompare(b.name)).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <button className="link-btn"
                        onClick={() => setEditing({ name: v.recorded_as, model: 'unknown', threat: 'unknown' })}>
                  Add as new
                </button>
              </li>
            ))}
          </ul>
          {linkMsg && <p className="cx-linkmsg" role="status">{linkMsg}</p>}
        </section>
      )}

      {/* The same search engine the Atlas uses, over a different catalogue. Nothing in the
          builder knows what a competitor is. */}
      <div className="cx-findbar">
        <button className="btn" onClick={() => setFinding(!finding)}>
          {finding ? 'Back to the grid' : 'Find by criteria'}
        </button>
      </div>

      {finding && (
        <div className="card">
          <SearchBuilder<SearchableCompetitor>
            rows={competitors.map((c) => ({
              ...c,
              offeringCount: offerings.filter((o) => o.competitor_id === c.id).length,
              categories: [...new Set(offerings.filter((o) => o.competitor_id === c.id)
                                               .map((o) => o.category))],
            })) as unknown as SearchableCompetitor[]}
            fields={competitorFields()}
            presets={COMPETITOR_PRESETS as never}
            columns={['Competitor', 'Sells', 'Threat', 'Products', 'Weakness']}
            fallbackSort={{ field: 'offerings', direction: 'desc' }}
            renderRow={(h) => (
              <>
                <td>
                  <button className="link-btn"
                          onClick={() => setOpenId((h.row as unknown as Competitor).id)}>
                    {h.row.name}
                  </button>
                  {h.row.parent_name && <div className="muted">{h.row.parent_name}</div>}
                </td>
                <td>{h.row.model}</td>
                <td><span className={`pill ${h.row.threat === 'primary' ? 'risk' : 'mute'}`}>
                  {h.row.threat}</span></td>
                <td className="num">{h.row.offeringCount}</td>
                <td className="cx-weak">{h.row.their_weakness
                  ? String(h.row.their_weakness).slice(0, 90) : <span className="muted">not recorded</span>}</td>
              </>
            )}
          />
        </div>
      )}

      {/* The grid of small summaries. Each card is two controls, not one: the body opens
          the full record, the footer files a document without opening anything. */}
      {!finding && (<div className="cx-grid">
        {shown.map((c) => {
          const products = offerings.filter((o) => o.competitor_id === c.id).length;
          const seen = presence.find((p) => p.competitor_id === c.id);
          const docs = docCounts[c.id] ?? 0;
          return (
            <article key={c.id} className="cx-mini card">
              <button className="cx-mini-open" onClick={() => setOpenId(c.id)}>
                <span className="cx-mini-name">{c.name}</span>
                {c.parent_name && <span className="cx-parent">part of {c.parent_name}</span>}
                <span className="cx-tags">
                  <span className={`pill ${THREAT_TONE[c.threat] ?? 'mute'}`}>{c.threat}</span>
                  <span className="pill mute" title={MODELS.find((m) => m.key === c.model)?.note}>
                    {MODELS.find((m) => m.key === c.model)?.label ?? c.model}
                  </span>
                </span>
                <span className="cx-mini-meta">
                  {products > 0 && <span>{products} product{products === 1 ? '' : 's'}</span>}
                  {seen && seen.sites > 0 && (
                    <span>met at {seen.sites} centre{seen.sites === 1 ? '' : 's'}</span>
                  )}
                  <span>{docs === 0 ? 'no documents' : `${docs} document${docs === 1 ? '' : 's'}`}</span>
                </span>
              </button>
              <footer className="cx-mini-foot">
                <button className="link-btn" onClick={() => setOpenId(c.id)}>Open</button>
                <button className="link-btn" onClick={() => setAddingDocsId(c.id)}>
                  Add document
                </button>
              </footer>
            </article>
          );
        })}
      </div>)}

      {events.length > 0 && (
        <section className="cx-events">
          <h2>What has moved</h2>
          <ol>
            {events.map((e) => (
              <li key={e.id}>
                <div className="cx-when">
                  {new Date(e.occurred_on).toLocaleDateString('en-GB',
                    { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <div className="cx-what">
                  <strong>{e.headline}</strong>
                  {e.detail && <p>{e.detail}</p>}
                  {/* An event with no consequence recorded is a news clipping. */}
                  {e.so_what && <p className="cx-sowhat"><span>So what:</span> {e.so_what}</p>}
                  <div className="cx-src">
                    {e.source_url
                      ? <a href={e.source_url} target="_blank" rel="noreferrer">{e.source_name}</a>
                      : e.source_name}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {open && (
        <CompetitorDetail
          row={open}
          offerings={offerings.filter((o) => o.competitor_id === open.id)}
          seen={presence.find((p) => p.competitor_id === open.id)}
          onClose={() => setOpenId(null)}
          onEdit={() => setEditing(open)}
          onAddDocument={() => setAddingDocsId(open.id)}
        />
      )}

      {addingDocs && (
        <AddDocumentDialog
          row={addingDocs}
          onClose={() => setAddingDocsId(null)}
          onChanged={() => refreshDocCounts(competitors)}
        />
      )}

      {editing && (
        <CompetitorDialog row={editing} onClose={() => setEditing(null)}
                          onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

/* =====================================================================================
   The full intelligence record: everything the summary card left out, in an overlay. This
   IS the view that used to be the grid card, plus the documents, so nothing a reader could
   see before has been taken away; it has moved one click deeper and gained the file.
   ===================================================================================== */
function CompetitorDetail({ row, offerings, seen, onClose, onEdit, onAddDocument }: {
  row: Competitor; offerings: Offering[]; seen: Presence | undefined;
  onClose: () => void; onEdit: () => void; onAddDocument: () => void;
}) {
  const ref = useDialog(onClose);
  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf cx-detail" role="dialog" aria-modal="true" aria-label={row.name} ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head">
          <div>
            <h2>{row.name}</h2>
            {row.parent_name && <span className="cx-parent">part of {row.parent_name}</span>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cf-body cx-detail-body">
          <div className="cx-tags">
            <span className={`pill ${THREAT_TONE[row.threat] ?? 'mute'}`}>{row.threat}</span>
            <span className="pill mute" title={MODELS.find((m) => m.key === row.model)?.note}>
              {MODELS.find((m) => m.key === row.model)?.label ?? row.model}
            </span>
          </div>

          {row.our_position && <p className="cx-position">{row.our_position}</p>}

          <div className="cx-swot">
            {row.their_strength && (
              <div><span className="cx-l">Strength</span><p>{row.their_strength}</p></div>
            )}
            {row.their_weakness && (
              <div><span className="cx-l">Weakness</span><p>{row.their_weakness}</p></div>
            )}
          </div>

          {offerings.length > 0 && (
            <ul className="cx-offerings">
              {offerings.map((o) => (
                <li key={o.id}>
                  <strong>{o.product_name ?? CATEGORIES.find((x) => x.key === o.category)?.label}</strong>
                  <span className="cx-cat">{CATEGORIES.find((x) => x.key === o.category)?.label ?? o.category}</span>
                  {o.regulatory && <span className="cx-reg">{o.regulatory}</span>}
                </li>
              ))}
            </ul>
          )}

          {/* Derived from the field rather than typed here. */}
          {seen && seen.sites > 0 && (
            <div className="cx-seen">
              Met at <strong>{seen.sites}</strong> centre{seen.sites === 1 ? '' : 's'}
              {seen.marked_displaceable > 0 && (
                <span className="pill ok">{seen.marked_displaceable} marked displaceable</span>
              )}
              {seen.earliest_window && (
                <span className="cx-window">next window {seen.earliest_window}</span>
              )}
            </div>
          )}

          {/* What documents actually say about them, with the sentence and the page. Sits
              below our own assessment deliberately: the judgement above is ours and the
              evidence below is somebody else's, and a reader should never have to guess
              which is which. */}
          <RecordEvidence kind="company" id={row.id} />

          {/* The repository, read-only here. Adding goes through the one screen that shows
              what is already filed first, so there is exactly one path onto the pile and it
              is the one with the duplicate check on it. */}
          <Attachments
            objectType="competitor"
            objectId={row.id}
            bucket="circus"
            label="Documents"
            readOnly
          />
          <div className="cx-detail-adddoc">
            <button className="btn" onClick={onAddDocument}>Add document</button>
          </div>

          <footer className="cx-foot">
            {row.source_url
              ? <a href={row.source_url} target="_blank" rel="noreferrer">{row.source_name}</a>
              : <span>{row.source_name}</span>}
            <span className={`conf ${row.confidence}`}>{row.confidence}</span>
            <button className="link-btn" onClick={onEdit}>Edit</button>
          </footer>
        </div>
      </div>
    </>
  );
}

/* =====================================================================================
   Add a document, without opening the record.

   WHAT IS ALREADY FILED COMES FIRST, name and description, because the duplicate this
   screen exists to prevent is a person re-uploading a document that is already here under
   a different filename. The description is what makes that check possible, so it is
   required here: "final_v3 (2).pdf" recognises nothing.

   ONE FILE AT A TIME, deliberately. The description belongs to the file it describes, and
   a batch upload with one shared description is how ten files end up labelled "congress
   material" and the eleventh gets filed again next week.

   THE TAB WARNING. Two tabs open on this screen for the same competitor cannot see each
   other's uploads until one reloads, which defeats the list above. Tab presence is
   detected via a BroadcastChannel (localStorage heartbeat as fallback) keyed on the
   competitor id, and the warning names the hazard rather than blocking: the person with
   two tabs open knows which one to close, and this code does not.
   ===================================================================================== */
function AddDocumentDialog({ row, onClose, onChanged }: {
  row: Competitor; onClose: () => void; onChanged: () => void;
}) {
  const ref = useDialog(onClose);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [desc, setDesc] = useState('');
  const [role, setRole] = useState<AttachmentRole>('evidence');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const live = BACKEND === 'supabase';
  const elsewhere = useTabPresence(`circus.add-document.${row.id}`);
  const ready = desc.trim().length > 2;

  const reloadFiles = useCallback(async () => {
    try { setFiles(await fetchAttachments('competitor', row.id)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setListLoading(false); }
  }, [row.id]);
  useEffect(() => { void reloadFiles(); }, [reloadFiles]);

  async function take(list: FileList | null) {
    const file = list?.[0];
    if (!file || !ready) return;
    setErr(null); setBusy(file.name);
    try {
      await uploadFile({
        file, bucket: 'circus', objectType: 'competitor', objectId: row.id,
        role, description: desc,
      });
      setDesc('');
      await reloadFiles();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
      if (input.current) input.current.value = '';
    }
  }

  async function openFile(a: Attachment) {
    try { window.open(await signedUrl(a.bucket, a.path), '_blank', 'noopener'); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault(); setOver(false);
    void take(e.dataTransfer.files);
  }

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf cx-adddoc" role="dialog" aria-modal="true"
           aria-label={`Documents for ${row.name}`} ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head">
          <h2>Documents: {row.name}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cf-body">
          {elsewhere && (
            <p className="cxd-warn" role="alert">
              This add-document screen is already open in another tab or window for the same
              competitor. Neither tab can see what the other uploads until it is reopened,
              which is exactly how the same file gets filed twice. Use one tab.
            </p>
          )}

          <h3 className="cxd-h">Already filed</h3>
          <p className="cf-note">
            Check this list before adding anything. Most duplicates are a document somebody
            already filed under a different filename, and the description is what makes the
            check possible.
          </p>

          {listLoading && <p className="quiet-loading">Loading</p>}
          {!listLoading && files.length === 0 && (
            <p className="att-empty">Nothing filed yet for {row.name}.</p>
          )}
          {files.length > 0 && (
            <ul className="cxd-list">
              {files.map((a) => (
                <li key={a.id} className="cxd-item">
                  <div className="cxd-main">
                    <button className="att-name" onClick={() => openFile(a)} title="Open">
                      {a.filename}
                    </button>
                    {a.description
                      ? <span className="cxd-desc">{a.description}</span>
                      : <span className="cxd-desc muted">No description recorded</span>}
                  </div>
                  <span className="att-meta">
                    {a.role !== 'attachment' && <span className="pill mute">{a.role}</span>}
                    {humanSize(a.bytes)}
                    {' · '}{a.uploaded_by_name ?? 'someone'}
                    {' · '}{new Date(a.uploaded_at).toLocaleDateString('en-GB')}
                  </span>
                  <button className="att-remove"
                          onClick={() => removeAttachment(a.id).then(reloadFiles).then(onChanged)}
                          title="Withdraw. The file stays retrievable for audit.">
                    Withdraw
                  </button>
                </li>
              ))}
            </ul>
          )}

          <h3 className="cxd-h">Add one</h3>
          {!live ? (
            <p className="cf-note">
              This demonstration build does not store files. On a live tenant the document
              and its description are filed here.
            </p>
          ) : (
            <>
              <div className="cf-row">
                <label className="cf-field"><span>What is it (required)</span>
                  <input value={desc} maxLength={500}
                         onChange={(e) => setDesc(e.target.value)}
                         placeholder="One line the next person can recognise it by" /></label>
                <label className="cf-field cxd-role"><span>Meaning</span>
                  <select value={role} onChange={(e) => setRole(e.target.value as AttachmentRole)}>
                    <option value="evidence">evidence</option>
                    <option value="attachment">attachment</option>
                  </select></label>
              </div>

              <div
                className={`att-drop ${over ? 'over' : ''} ${busy ? 'busy' : ''} ${ready ? '' : 'cxd-wait'}`}
                onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                onDragLeave={() => setOver(false)}
                onDrop={onDrop}
                onClick={() => ready && input.current?.click()}
                role="button" tabIndex={0} aria-disabled={!ready}
                onKeyDown={(e) => e.key === 'Enter' && ready && input.current?.click()}
              >
                <input ref={input} type="file" hidden
                       accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                       onChange={(e) => take(e.target.files)} />
                {busy ? `Uploading ${busy}…`
                  : ready ? 'Drop one file here, or click to choose'
                  : 'Describe the document first, then add the file'}
              </div>

              <p className="cf-note">
                One file per description, deliberately: the description belongs to the file
                it describes. Public sources only, on the same rule as the record itself.
              </p>
            </>
          )}

          {err && <p className="att-err" role="alert">{err}</p>}
        </div>
      </div>
    </>
  );
}

function CompetitorDialog({ row, onClose, onSaved }: {
  row: Partial<Competitor>; onClose: () => void; onSaved: () => void;
}) {
  const ref = useDialog(onClose);
  const [f, setF] = useState<Partial<Competitor>>(row);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof Competitor) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value || null } as Partial<Competitor>);

  async function save() {
    setBusy(true); setErr(null);
    try { await saveCompetitor(f); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label="Competitor" ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head"><h2>{f.id ? 'Edit competitor' : 'Add a competitor'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="cf-body">
          <div className="cf-row">
            <label className="cf-field"><span>Name</span>
              <input value={f.name ?? ''} onChange={set('name')} /></label>
            <label className="cf-field"><span>Parent, if any</span>
              <input value={f.parent_name ?? ''} onChange={set('parent_name')}
                     placeholder="Eurobio Scientific" /></label>
          </div>
          <div className="cf-row">
            <label className="cf-field"><span>What they sell</span>
              <select value={f.model ?? 'unknown'} onChange={set('model')}>
                {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select></label>
            <label className="cf-field"><span>Threat</span>
              <select value={f.threat ?? 'unknown'} onChange={set('threat')}>
                {['primary', 'significant', 'watch', 'peripheral', 'unknown'].map((t) =>
                  <option key={t} value={t}>{t}</option>)}
              </select></label>
          </div>
          <p className="cf-note">
            What they sell decides who buys it and from which budget. Different buyer, different
            budget, different sale, and a company can move between them.
          </p>
          <label className="cf-field"><span>Where we stand against them</span>
            <textarea rows={2} value={f.our_position ?? ''} onChange={set('our_position')} /></label>
          <div className="cf-row">
            <label className="cf-field"><span>Their strength</span>
              <textarea rows={2} value={f.their_strength ?? ''} onChange={set('their_strength')} /></label>
            <label className="cf-field"><span>Their weakness</span>
              <textarea rows={2} value={f.their_weakness ?? ''} onChange={set('their_weakness')} /></label>
          </div>
          <div className="cf-row">
            <label className="cf-field"><span>Source (required)</span>
              <input value={f.source_name ?? ''} onChange={set('source_name')}
                     placeholder="Company press release, 7 July 2026" /></label>
            <label className="cf-field"><span>Source URL</span>
              <input value={f.source_url ?? ''} onChange={set('source_url')} /></label>
          </div>
          <p className="cf-note">
            A public source is required on every record. Competitor information obtained
            confidentially is a liability for the company holding it, not an advantage.
          </p>

          {/* THE DOCUMENT REPOSITORY.

              Only once the competitor exists, because an attachment has to belong to something
              and a record that has never been saved has no id to hang a file on. Offering the
              control before the first save would produce an upload with nowhere to go, which is
              the failure mode the technical panel had.

              The same public-source discipline applies to the files. A competitor's own investor
              deck, a congress panel photographed from the floor and a regulatory clearance letter
              are all public. A document that reached us another way does not belong here, and
              storing it would convert an advantage into a liability. */}
          {f.id ? (
            <div className="cf-files">
              <Attachments
                objectType="competitor"
                objectId={f.id}
                bucket="circus"
                label="Documents"
                roles={['evidence', 'attachment']}
                accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                describe
              />
              <p className="cf-note">
                Investor decks, published clearances, congress material and brochures. Public
                sources only, on the same rule as the record itself.
              </p>
            </div>
          ) : (
            <p className="cf-note">Save the competitor first, then documents can be attached.</p>
          )}
          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-save" onClick={save}
                    disabled={busy || !f.name?.trim() || !f.source_name?.trim()}>
              {busy ? 'Saving' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export function Circus() {
  return <Boundary what="Circus"><CircusView /></Boundary>;
}
